import './bootstrap';
import '../css/app.css';

import type { DefineComponent } from 'vue';
import { createApp, h } from 'vue';
import { createInertiaApp } from '@inertiajs/vue3';
import AppLayout from '@/Layouts/AppLayout.vue';
// @ts-ignore
import vClickOutside from 'click-outside-vue3';
import { createPinia } from 'pinia';
import VueObserveVisibility from 'vue-observe-visibility';
import mixpanel from 'mixpanel-browser';
import VueMapboxTs from 'vue-mapbox-ts';
import * as Sentry from '@sentry/vue';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import { ZiggyVue } from 'ziggy-js';
import handleBrokenImage from './global';
import { configureEcho } from '@laravel/echo-vue';
import { suppressInertiaInvalidModal } from '@/Support/suppressInertiaInvalidModal';
import { suppressInertiaNetworkErrors } from '@/Support/suppressInertiaNetworkErrors';

suppressInertiaInvalidModal();
suppressInertiaNetworkErrors();

const appName = import.meta.env.VITE_APP_NAME || 'Property Reports';

import.meta.glob([
  '../images/**',
]);

createInertiaApp({
  title: (title) => `${title} - ${appName}`,
  resolve: async (name) => {
    const page = await resolvePageComponent(`./Pages/${name}.vue`, import.meta.glob<DefineComponent>('./Pages/**/*.vue'));
    page.default.layout = page.default.layout || AppLayout;
    return page;
  },
  setup({
    el, App, props, plugin,
  }) {
    const app = createApp({ render: () => h(App, props) });

    const { user } = props.initialPage.props.auth;

    if (user) {
      Sentry.setUser({
        id: user.id as string,
        email: user.email,
      });
    }

    Sentry.init({
      app,
      dsn: import.meta.env.VITE_SENTRY_DSN_PUBLIC,
      environment: import.meta.env.MODE,
      // Filter out noise from Facebook's in-app browser (IAB). When the IAB
      // closes, Facebook's injected navigation_performance_logger_android
      // script fires a teardown handler after the Java bridge has already been
      // released, causing an unhandled "Java object is gone" error that
      // originates entirely within Facebook's own injected code (iabjs://
      // scheme) and does not reflect any issue in our application.
      ignoreErrors: [
        /Java object is gone/,
        /Error invoking postMessage/,
      ],
      denyUrls: [
        /^iabjs:\/\//,
        // Filter out errors originating from browser extensions. These errors
        // are thrown inside the extension's own sandboxed scripts (e.g.
        // chrome-extension:// or moz-extension://) and are not caused by our
        // application code. They appear when third-party extensions (crypto
        // wallets, ad-blockers, etc.) interact with pages they are injected
        // into, and produce false-positive noise in our Sentry queue.
        /^chrome-extension:\/\//,
        /^moz-extension:\/\//,
      ],
    });

    app.use(plugin)
      .use(vClickOutside)
      .use(VueObserveVisibility)
      .use(createPinia())
      .use(ZiggyVue)
      .use(VueMapboxTs);

    app.config.globalProperties.$handleBrokenImage = handleBrokenImage;

    if (import.meta.env.PROD) {
      mixpanel.init(import.meta.env.VITE_MIXPANEL_PROJECT_TOKEN, {
        persistence: 'localStorage',
      });
    }

    configureEcho({
      broadcaster: 'pusher',
      key: props.initialPage.props.config.broadcasting.connections.pusher.key,
      wsPort: 443,
      wssPort: 443,
      forceTLS: true,
      enabledTransports: ['ws', 'wss'],
      cluster: 'eu',
    });

    app.mount(el);
  },
  progress: {
    color: '#1233C9',
  },
});
